/* eslint-disable react/function-component-definition */
/* Global imports. */
import React from "react";
import { Link } from "gatsby";

import YouTubeEmbed from "@components/YouTubeEmbed";

import KontentFeaturedContent from "@models/kontent/KontentFeaturedContent";
import { KontentArticleSummary } from "@src/models/kontent";

import {preferredDisplayContains} from "@src/helpers"

interface Props {
  featuredContent: KontentFeaturedContent;
  article: KontentArticleSummary;
  articleSlug: string;
  componentID?: string;
}

const Image = ({imageUrl, imageType, imagePlacement}) => {
  const backgroundImageStyle = {
    height: "100%",
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: "cover",
    backgroundPosition: "center"
  }

  return imageType === "background" ? 
  <div className={`${imagePlacement === "right" ? "col-start-2 row-start-1" : "col-start-1 row-start-1"} content-image-background pb-[50%] sm:pb-0`} style={backgroundImageStyle} /> : 
  <div className={`content-image ${imagePlacement === "right" ? "col-start-2 row-start-1" : "col-start-1 row-start-1"} `}>{imageUrl && <img src={imageUrl} className="w-full" alt="" />}</div>
}

const ImageWithLink = ({itemLink, imageUrl, imageType, imagePlacement}) => <Link to={itemLink}><Image imageUrl={imageUrl} imagePlacement={imagePlacement} imageType={imageType} /></Link>

const TextContent = ({backgroundText, itemSource, textStyle, itemTitle, itemLink, itemBlurb, buttonColourStyle, buttonText, itemDisplayDate, preferredDisplayArray=[]}) => <span className="flex flex-col items-start justify-center h-full p-6 text-left md:p-8" style={{backgroundColor: `${backgroundText}`}}>
          {itemSource && preferredDisplayContains(preferredDisplayArray, "source") && <span className="category-meta" style={textStyle}>{itemSource}</span>}

          <Link to={itemLink} className="!no-underline"><h3 style={textStyle}>{itemTitle && itemTitle}</h3></Link>
          {itemBlurb && preferredDisplayContains(preferredDisplayArray, "description") && 
          <div className="content-description" style={textStyle} dangerouslySetInnerHTML={{ __html: itemBlurb && itemBlurb }}/>
          }
          {
            itemLink && preferredDisplayContains(preferredDisplayArray, "button") && 
            <Link to={itemLink} className="my-6 ml-0 sm:my-0 button !no-underline" style={buttonColourStyle}>{buttonText}</Link>
          }
          {itemDisplayDate && preferredDisplayContains(preferredDisplayArray, "date") && 
          <span className="blog-date" style={textStyle}>{itemDisplayDate}</span>
          }
        </span> 

const FeaturedContent = ({ featuredContent, article, articleSlug, componentID}: Props) => {
  const typeInlineImage = "inline_image"
  const typeEmbed = "embed"

  const bannerWidth = featuredContent.display_width && featuredContent.display_width.value && featuredContent.display_width.value[0] && featuredContent.display_width.value[0].codename
  const bannerType = featuredContent && featuredContent.type && featuredContent.type.value && featuredContent.type.value[0] && featuredContent.type.value[0].codename ? featuredContent.type.value[0].codename : featuredContent.image_type && featuredContent.image_type.value && featuredContent.image_type.value[0] && featuredContent.image_type.value[0].codename
  const preferredDisplayArray = featuredContent.preferred_display && featuredContent.preferred_display.value
  const imagePlacement = featuredContent.image_position && featuredContent.image_position.value && featuredContent.image_position.value[0] ? featuredContent.image_position.value[0].codename : "left";
  const imageType = featuredContent.image_type && featuredContent.image_type.value && featuredContent.image_type.value[0] ? featuredContent.image_type.value[0].codename : "background"
  const textPlacementClass = featuredContent.text_placement && featuredContent.text_placement.value && featuredContent.text_placement.value[0] ? `banner_text_${featuredContent.text_placement.value[0].codename}` : ""
  const backgroundColourLeft = featuredContent.background_colour_left && featuredContent.background_colour_left.value ? featuredContent.background_colour_left.value : "#FFF"
  const backgroundColourRight = featuredContent.background_colour_right && featuredContent.background_colour_right.value ? featuredContent.background_colour_right.value : "#FFF"
  const backgroundText = featuredContent.text_background_colour && featuredContent.text_background_colour.value ? featuredContent.text_background_colour.value : "transparent"
  const buttonText = featuredContent.url_text && featuredContent.url_text.value ? featuredContent.url_text.value : "Read more"
  const buttonBackgroundColour = featuredContent.button_colour && featuredContent.button_colour.value
  const buttonTextColour = featuredContent.button_text_colour && featuredContent.button_text_colour.value
  const imageUrl = featuredContent.image && featuredContent.image.value && featuredContent.image.value[0] && featuredContent.image.value[0].url ? featuredContent.image.value[0].url : article && article.image && article.image.value && article.image.value[0] && article.image.value[0].url
  const itemTitle = featuredContent.title && featuredContent.title.value ? featuredContent.title.value : article && article.title && article.title.value
  const itemBlurb = featuredContent.blurb && featuredContent.blurb.value && featuredContent.blurb.value !== "<p><br></p>" ? featuredContent.blurb.value : article && article.blurb && article.blurb.value
  const itemLink = featuredContent.url && featuredContent.url.value ? featuredContent.url.value : article && articleSlug && `/${articleSlug}`
  const itemSource = featuredContent.source && featuredContent.source.value ? featuredContent.source.value : article && article.article_categories && article.article_categories.value && article.article_categories.value[0] && article.article_categories.value[0].name
  const itemDisplayDate = featuredContent.display_date && featuredContent.display_date.value ? featuredContent.display_date.value : article && article.display_date && article.display_date.value

  const textStyle = {
    color: featuredContent.text_colour && featuredContent.text_colour.value ? featuredContent.text_colour.value : "#000000"
  }

  const buttonColourStyle = {
    backgroundColor: buttonBackgroundColour,
    borderColor: buttonBackgroundColour,
    color: buttonTextColour
  }

  const bannerWidthFlexible = "flexible";
  const bannerIsFlexible = bannerWidth === bannerWidthFlexible
  const contentFlexibleClasses = "relative pl-0 pr-0 left-[50%] ml-[-50vw] mr-[-50vw] max-w-[100vw] right-[50%] w-[100vw]";
  const contentConstrainedClasses = "relative";
  const bgLeftFlexibleClasses = "ml-[-50vw] w-full bottom-0 left-0 absolute right-2/4 top-0";
  const bgRightFlexibleClasses = "mr-[-50vw] w-full bottom-0 right-0 absolute left-2/4 top-0";
  const bgLeftConstrainedClasses ="bottom-0 left-0 top-0 right-1/2 absolute";
  const bgRightConstrainedClasses ="bottom-0 left-1/2 top-0 right-0 absolute";



  return (
    <div id={componentID} data-component="featured-content" className={`mb-8 ${bannerIsFlexible ? contentFlexibleClasses : contentConstrainedClasses}`}>
      <div className={`${bannerIsFlexible ? "sm:max-w-full lg:max-w-[1000px] xl:max-w-[1100px] ml-auto mr-auto " : "ml-auto mr-auto "}`}>
      <section className={`p-0 mt-0 mb-0 ml-auto mr-auto flex-wrap relative text-center w-full flex content-center bg-cover ${textPlacementClass} ${!bannerIsFlexible ? "rounded-lg overflow-hidden shadow-card" : ""}`} >
          {backgroundColourLeft !== "transparent" && <span className={bannerIsFlexible ?bgLeftFlexibleClasses : bgLeftConstrainedClasses} style={{backgroundColor: `${backgroundColourLeft}`}} />}
          {backgroundColourRight !== "transparent" && <span className={bannerIsFlexible ?bgRightFlexibleClasses : bgRightConstrainedClasses} style={{backgroundColor: `${backgroundColourRight}`}} />}

          <div className={`grid w-full group relative ${bannerType === typeEmbed ? "sm:grid-cols-3" : "sm:grid-cols-2"} ${imagePlacement === "left" ? "" : ""}`}>
            {
              bannerType === typeInlineImage && ( 
                itemLink && itemLink !== undefined ? <ImageWithLink itemLink={itemLink}  imageUrl={imageUrl} imagePlacement={imagePlacement} imageType={imageType} /> : <Image imageUrl={imageUrl} imagePlacement={imagePlacement} imageType={imageType} />
              )
            }
            {
              bannerType === typeEmbed && (
                <div className={`${imagePlacement === "right" && "sm:col-start-2 row-start-1"} ${bannerType === typeEmbed ? "col-span-2" : "col-span-1"}`} style={{backgroundColor: `${imagePlacement === "right" ? backgroundColourRight : backgroundColourLeft}`}}>
                  {featuredContent.youtube && featuredContent.youtube.value && featuredContent.youtube.value[0] && <YouTubeEmbed id={featuredContent.youtube.value[0].elements.video_id.value} title={featuredContent.youtube.value[0].elements.title.value} bottomMargin={false} />}
                </div>
              )
            } 
            <TextContent backgroundText={backgroundText} itemSource={itemSource} textStyle={textStyle} itemTitle={itemTitle} itemLink={itemLink} itemBlurb={itemBlurb} buttonColourStyle={buttonColourStyle} buttonText={buttonText} itemDisplayDate={itemDisplayDate} preferredDisplayArray={preferredDisplayArray} />
          </div>
      </section>
      </div>
    </div>

  )
};

export default FeaturedContent;
